import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton,
  Alert,
  Snackbar,
  Checkbox,
  Grid
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import api from '../../utils/api';

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'mechanic',
    active: true,
    accessRentals: true,
    accessMaintenance: true,
    accessSales: true,
    accessShop: false,
    accessAccounting: false
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (err) {
      console.error('Errore nel recupero degli utenti:', err);
      showSnackbar('Errore nel recupero degli utenti', 'error');
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
        active: user.active,
        accessRentals: user.accessRentals !== undefined ? user.accessRentals : true,
        accessMaintenance: user.accessMaintenance !== undefined ? user.accessMaintenance : true,
        accessSales: user.accessSales !== undefined ? user.accessSales : true,
        accessShop: user.accessShop !== undefined ? user.accessShop : false,
        accessAccounting: user.accessAccounting !== undefined ? user.accessAccounting : false
      });
      setSelectedUser(user);
    } else {
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'mechanic',
        active: true,
        accessRentals: true,
        accessMaintenance: true,
        accessSales: true,
        accessShop: false,
        accessAccounting: false
      });
      setSelectedUser(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'mechanic',
      active: true,
      accessRentals: true,
      accessMaintenance: true,
      accessSales: true,
      accessShop: false,
      accessAccounting: false
    });
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'active' ? checked : value
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Validazione base lato client
      if (!formData.name.trim()) {
        showSnackbar('Il nome è obbligatorio', 'error');
        setLoading(false);
        return;
      }
      
      if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email.trim())) {
        showSnackbar('Email non valida', 'error');
        setLoading(false);
        return;
      }
      
      // Richiedi password solo per nuovi utenti
      if (!selectedUser && !formData.password.trim()) {
        showSnackbar('La password è obbligatoria per i nuovi utenti', 'error');
        setLoading(false);
        return;
      }
      
      // Verifica che i ruoli siano stati selezionati
      if (!formData.role) {
        showSnackbar('Il ruolo è obbligatorio', 'error');
        setLoading(false);
        return;
      }
      
      if (selectedUser) {
        // Aggiorna utente esistente
        const dataToUpdate = {...formData};
        // Se la password è vuota, rimuovila dalla richiesta
        if (!dataToUpdate.password.trim()) {
          delete dataToUpdate.password;
        }
        console.log('Aggiornamento utente con ID:', selectedUser.id);
        const response = await api.put(`/users/${selectedUser.id}`, dataToUpdate);
        console.log('Risposta aggiornamento:', response.data);
        showSnackbar('Utente aggiornato con successo');
      } else {
        // Crea nuovo utente
        console.log('Creazione nuovo utente');
        const response = await api.post('/users', formData);
        console.log('Risposta creazione:', response.data);
        showSnackbar('Utente creato con successo');
      }
      handleCloseDialog();
      fetchUsers();
    } catch (err) {
      console.error('Errore nel salvataggio dell\'utente:', err);
      if (err.response && err.response.data) {
        if (err.response.data.msg) {
          showSnackbar(`Errore: ${err.response.data.msg}`, 'error');
        } else if (err.response.data.errors && err.response.data.errors.length > 0) {
          const errorMessages = err.response.data.errors.map(e => e.msg).join(', ');
          showSnackbar(`Errore: ${errorMessages}`, 'error');
        } else {
          showSnackbar(`Errore ${err.response.status}: ${err.response.statusText}`, 'error');
        }
      } else {
        showSnackbar('Errore nel salvataggio dell\'utente', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Sei sicuro di voler eliminare questo utente?')) {
      try {
        await api.delete(`/users/${userId}`);
        showSnackbar('Utente eliminato con successo');
        fetchUsers();
      } catch (err) {
        console.error('Errore nell\'eliminazione dell\'utente:', err);
        showSnackbar('Errore nell\'eliminazione dell\'utente', 'error');
      }
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Gestione Utenti</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nuovo Utente
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Ruolo</TableCell>
              <TableCell>Stato</TableCell>
              <TableCell>Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.role}</TableCell>
                <TableCell>{user.active ? 'Attivo' : 'Disattivato'}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpenDialog(user)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(user.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedUser ? 'Modifica Utente' : 'Nuovo Utente'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              name="name"
              label="Nome"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              name="email"
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              fullWidth
              required
              disabled={!!selectedUser}
            />
            <TextField
              name="password"
              label="Password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              fullWidth
              required={!selectedUser}
              helperText={selectedUser ? 'Lascia vuoto per mantenere la password attuale' : ''}
            />
            <FormControl fullWidth>
              <InputLabel>Ruolo</InputLabel>
              <Select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                label="Ruolo"
              >
                <MenuItem value="superadmin">Super Admin</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="mechanic">Meccanico</MenuItem>
                <MenuItem value="sales">Venditore</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                />
              }
              label="Utente Attivo"
            />

            {/* Sezione Permessi - Solo se non è superadmin/supersaiyan */}
            {!['superadmin', 'supersaiyan'].includes(formData.role) && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Permessi di Accesso alle Sezioni
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                  Seleziona le sezioni accessibili. Superadmin e Supersaiyan hanno accesso completo.
                </Typography>
                
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.accessRentals || false}
                          onChange={(e) => setFormData(prev => ({ ...prev, accessRentals: e.target.checked }))}
                          name="accessRentals"
                          color="primary"
                        />
                      }
                      label="Noleggi"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.accessMaintenance || false}
                          onChange={(e) => setFormData(prev => ({ ...prev, accessMaintenance: e.target.checked }))}
                          name="accessMaintenance"
                          color="primary"
                        />
                      }
                      label="Manutenzioni"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.accessSales || false}
                          onChange={(e) => setFormData(prev => ({ ...prev, accessSales: e.target.checked }))}
                          name="accessSales"
                          color="primary"
                        />
                      }
                      label="Vendite"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.accessShop || false}
                          onChange={(e) => setFormData(prev => ({ ...prev, accessShop: e.target.checked }))}
                          name="accessShop"
                          color="primary"
                        />
                      }
                      label="Shop"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.accessAccounting || false}
                          onChange={(e) => setFormData(prev => ({ ...prev, accessAccounting: e.target.checked }))}
                          name="accessAccounting"
                          color="primary"
                        />
                      }
                      label="Contabilità"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersManagement; 